import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, MapPin, Users, GraduationCap, Buildings, Briefcase, ListChecks, Calendar, ArrowSquareOut, CurrencyCircleDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { formatDate, formatTime, formatLabel, formatCLP } from "../lib/format";
import CommunityDashboard from "../components/CommunityDashboard";
import { TabLabel } from "../components/DetailHelpers";

const EDU_TIPOS = [
  { value: "jardin", label: "Jardín infantil" },
  { value: "basica", label: "Escuela básica" },
  { value: "liceo", label: "Liceo" },
];
const ORG_TIPOS = [
  { value: "junta_vecinos", label: "Junta de vecinos" },
  { value: "buenos_vecinos", label: "Buenos vecinos" },
  { value: "adulto_mayor", label: "Adulto mayor" },
  { value: "indigena", label: "Organización indígena" },
  { value: "otra", label: "Otra organización" },
];
const INST_TIPOS = [
  { value: "carabineros", label: "Carabineros" },
  { value: "bomberos", label: "Bomberos" },
  { value: "posta", label: "Posta / Salud" },
  { value: "otra", label: "Otra" },
];

export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/communities/${id}`);
    setData(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="p-8 text-slate-500">Cargando…</div>;
  const { community, educational_facilities, community_organizations, local_institutions, commitments, projects, meetings } = data;

  return (
    <div data-testid="community-detail-page">
      <PageHeader
        title={community.name}
        description={`${community.tipo_vinculo} · ${community.region || ""} ${community.comuna ? `· ${community.comuna}` : ""}`}
        actions={
          <Link to="/comunidades">
            <Button variant="outline">Volver al listado</Button>
          </Link>
        }
      />

      <div className="px-6 py-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard icon={Users} label="Habitantes" value={community.habitantes || 0} />
          <SummaryCard icon={Users} label="Hombres / Mujeres" value={`${community.hombres || 0} / ${community.mujeres || 0}`} />
          <SummaryCard icon={ListChecks} label="Compromisos" value={commitments.length} />
          <SummaryCard icon={Briefcase} label="Proyectos" value={projects.length} />
          <SummaryCard
            icon={CurrencyCircleDollar}
            label="Inversión total"
            value={formatCLP(community.inversion_total || 0)}
            sublines={[
              `Proyectos: ${formatCLP(community.inversion_proyectos || 0)}`,
              `Compromisos: ${formatCLP(community.inversion_compromisos || 0)}`,
            ]}
          />
        </div>

        <Tabs defaultValue="dashboard">
          <TabsList>
            <TabsTrigger value="dashboard"><TabLabel label="Dashboard" hasData={(commitments.length + projects.length + meetings.length) > 0} /></TabsTrigger>
            <TabsTrigger value="info"><TabLabel label="Información" hasData /></TabsTrigger>
            <TabsTrigger value="edu"><TabLabel label={`Educación (${educational_facilities.length})`} hasData={educational_facilities.length > 0} /></TabsTrigger>
            <TabsTrigger value="orgs"><TabLabel label={`Organizaciones (${community_organizations.length})`} hasData={community_organizations.length > 0} /></TabsTrigger>
            <TabsTrigger value="inst"><TabLabel label={`Instituciones (${local_institutions.length})`} hasData={local_institutions.length > 0} /></TabsTrigger>
            <TabsTrigger value="commits"><TabLabel label={`Compromisos (${commitments.length})`} hasData={commitments.length > 0} /></TabsTrigger>
            <TabsTrigger value="projects"><TabLabel label={`Proyectos (${projects.length})`} hasData={projects.length > 0} /></TabsTrigger>
            <TabsTrigger value="meetings"><TabLabel label={`Reuniones (${meetings.length})`} hasData={meetings.length > 0} /></TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <CommunityDashboard
              community={community}
              commitments={commitments}
              projects={projects}
              meetings={meetings}
            />
          </TabsContent>

          <TabsContent value="info">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle>Datos generales</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Field label="País" value={community.pais} />
                <Field label="Región" value={community.region} />
                <Field label="Provincia" value={community.provincia} />
                <Field label="Comuna" value={community.comuna} />
                <Field label="Localización" value={community.localizacion} />
                <Field label="Tipo de vínculo" value={community.tipo_vinculo} />
                <Field label="Latitud / Longitud" value={community.latitude ? `${community.latitude}, ${community.longitude}` : "—"} />
                <div className="col-span-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Descripción</div>
                  <div className="mt-1 text-slate-800">{community.description || "—"}</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="edu">
            <SubResourceTable
              title="Establecimientos educacionales"
              icon={GraduationCap}
              data={educational_facilities}
              columns={["tipo","name","direccion","matricula","directivo"]}
              colLabels={{ tipo: "Tipo", name: "Nombre", direccion: "Dirección", matricula: "Matrícula", directivo: "Directivo" }}
              onAdd={async (form) => {
                await api.post(`/communities/${id}/educational`, form);
                load();
              }}
              onDelete={async (rid) => { await api.delete(`/educational/${rid}`); load(); }}
              addFields={[
                { name: "tipo", label: "Tipo", type: "select", options: EDU_TIPOS },
                { name: "name", label: "Nombre", type: "text", required: true },
                { name: "direccion", label: "Dirección", type: "text" },
                { name: "matricula", label: "Matrícula", type: "number" },
                { name: "directivo", label: "Directivo", type: "text" },
                { name: "contacto", label: "Contacto", type: "text" },
              ]}
              renderTipo={(t) => EDU_TIPOS.find(x => x.value === t)?.label || t}
            />
          </TabsContent>

          <TabsContent value="orgs">
            <SubResourceTable
              title="Organizaciones comunitarias"
              icon={Buildings}
              data={community_organizations}
              columns={["tipo","name","description"]}
              colLabels={{ tipo: "Tipo", name: "Nombre", description: "Descripción" }}
              onAdd={async (form) => {
                await api.post(`/communities/${id}/organizations`, { ...form, directiva: [], integrantes: [] });
                load();
              }}
              onDelete={async (rid) => { await api.delete(`/community_organizations/${rid}`); load(); }}
              addFields={[
                { name: "tipo", label: "Tipo", type: "select", options: ORG_TIPOS },
                { name: "name", label: "Nombre", type: "text", required: true },
                { name: "description", label: "Descripción", type: "textarea" },
              ]}
              renderTipo={(t) => ORG_TIPOS.find(x => x.value === t)?.label || t}
            />
          </TabsContent>

          <TabsContent value="inst">
            <SubResourceTable
              title="Instituciones presentes"
              icon={Buildings}
              data={local_institutions}
              columns={["tipo","name","direccion","contacto"]}
              colLabels={{ tipo: "Tipo", name: "Nombre", direccion: "Dirección", contacto: "Contacto" }}
              onAdd={async (form) => { await api.post(`/communities/${id}/institutions`, form); load(); }}
              onDelete={async (rid) => { await api.delete(`/local_institutions/${rid}`); load(); }}
              addFields={[
                { name: "tipo", label: "Tipo", type: "select", options: INST_TIPOS },
                { name: "name", label: "Nombre", type: "text", required: true },
                { name: "direccion", label: "Dirección", type: "text" },
                { name: "contacto", label: "Contacto", type: "text" },
                { name: "notas", label: "Notas", type: "textarea" },
              ]}
              renderTipo={(t) => INST_TIPOS.find(x => x.value === t)?.label || t}
            />
          </TabsContent>

          <TabsContent value="commits">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><ListChecks /> Compromisos vinculados</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/compromisos?related_entity_type=community&related_entity_id=${id}&new=1`} data-testid="comm-new-commitment">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nuevo compromiso</Button>
                  </Link>
                  <Link to={`/compromisos?related_entity_type=community&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Compromiso</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Inversión</TableHead><TableHead>Semáforo</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {commitments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Sin compromisos.</TableCell></TableRow>}
                    {commitments.map(c => (
                      <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/compromisos/${c.id}`)}>
                        <TableCell className="font-medium"><Link to={`/compromisos/${c.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{c.name}</Link></TableCell>
                        <TableCell><StatusBadge tone="info">{c.tipo}</StatusBadge></TableCell>
                        <TableCell><EstadoBadge value={c.estado} /></TableCell>
                        <TableCell>{formatDate(c.fecha_compromiso)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-700">{c.inversion ? formatCLP(c.inversion) : "—"}</TableCell>
                        <TableCell><Semaforo value={c.semaforo} /></TableCell>
                      </TableRow>
                    ))}
                    {commitments.some((c) => c.inversion) && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={4} className="text-right text-xs font-semibold text-slate-600">Total inversión compromisos</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-slate-900">
                          {formatCLP(commitments.reduce((s, c) => s + (Number(c.inversion) || 0), 0))}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="projects">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Briefcase /> Proyectos</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/proyectos?related_entity_type=community&related_entity_id=${id}&new=1`} data-testid="comm-new-project">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nuevo proyecto</Button>
                  </Link>
                  <Link to={`/proyectos?related_entity_type=community&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Proyecto</TableHead><TableHead>Eje</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Presupuesto</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {projects.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Sin proyectos.</TableCell></TableRow>}
                    {projects.map(p => (
                      <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/proyectos/${p.id}`)}>
                        <TableCell className="font-medium"><Link to={`/proyectos/${p.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{p.name}</Link></TableCell>
                        <TableCell>{p.eje_estrategico || "—"}</TableCell>
                        <TableCell><EstadoBadge value={p.estado} /></TableCell>
                        <TableCell className="text-right font-mono">{Number(p.presupuesto || 0).toLocaleString("es-CL")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="meetings">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Calendar /> Reuniones</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/reuniones?related_entity_type=community&related_entity_id=${id}&new=1`} data-testid="comm-new-meeting">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nueva reunión</Button>
                  </Link>
                  <Link to={`/reuniones?related_entity_type=community&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Asunto</TableHead><TableHead>Fecha</TableHead><TableHead>Lugar</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead className="w-12 text-right"><span className="sr-only">Acciones</span></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {meetings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Sin reuniones.</TableCell></TableRow>}
                    {meetings.map(m => (
                      <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/reuniones/${m.id}`)}>
                        <TableCell className="font-medium">
                          <Link to={`/reuniones/${m.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{m.asunto}</Link>
                          {m.acuerdos && <div className="text-xs text-slate-500 truncate max-w-md">{m.acuerdos.slice(0, 90)}{m.acuerdos.length > 90 ? "…" : ""}</div>}
                        </TableCell>
                        <TableCell>{formatDate(m.fecha)}{m.hora && <span className="text-xs text-slate-500 ml-1">{formatTime(m.hora)}</span>}</TableCell>
                        <TableCell>{m.lugar || "—"}</TableCell>
                        <TableCell>{m.tipo || "—"}</TableCell>
                        <TableCell><StatusBadge tone="info">{m.estado_seguimiento}</StatusBadge></TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Link to={`/reuniones/${m.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={14} /></Button></Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-slate-800">{value || "—"}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sublines }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <Icon size={20} weight="duotone" className="text-brand-orange-500" />
      </div>
      <div className="mt-3 font-display text-2xl font-bold">{value}</div>
      {sublines && sublines.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {sublines.map((s, i) => (
            <div key={i} className="text-[11px] text-slate-500">{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubResourceTable({ title, icon: Icon, data, columns, colLabels, addFields, onAdd, onDelete, renderTipo }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const submit = async () => {
    try { await onAdd(form); setOpen(false); setForm({}); toast.success("Agregado"); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <Card className="rounded-md mt-4">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Icon /> {title}</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-slate-900 text-white"><Plus /> Agregar</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {addFields.map(f => (
                <div key={f.name}>
                  <Label>{f.label}</Label>
                  {f.type === "select" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea rows={3} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                  ) : (
                    <Input type={f.type} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={submit} className="bg-slate-900 text-white">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(c => <TableHead key={c}>{colLabels[c]}</TableHead>)}
              <TableHead className="text-right">—</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-6 text-slate-500">Sin registros.</TableCell></TableRow>}
            {data.map(r => (
              <TableRow key={r.id}>
                {columns.map(c => (
                  <TableCell key={c}>{c === "tipo" && renderTipo ? renderTipo(r[c]) : (r[c] || "—")}</TableCell>
                ))}
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => onDelete(r.id)}><Trash size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
